import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError } from './api'

export interface Fetched<T> {
  data: T | null
  error: string | null
  loading: boolean
  reload: () => void
}

/**
 * Fetch a GET endpoint, with optional polling.
 *
 * Late responses from a superseded request are discarded so a slow reload
 * cannot overwrite fresher data.
 *
 * `loading` means "nothing has come back for this path yet" — NOT "a request
 * is in flight". A polled endpoint has a request in flight every few seconds
 * forever, and every page here renders its placeholder on
 * `loading && !data`, so setting it per poll made that placeholder blink on
 * and off at the polling interval. Harmless-looking when data is arriving,
 * because `!data` suppresses it; very visible when the endpoint is failing,
 * where `data` stays null and the placeholder flashes over the error message
 * on every retry. Reported on the Activity page while its API was returning
 * 503, and it would do the same on any page whose endpoint is down.
 *
 * A background refresh therefore leaves `loading` alone. What is on screen
 * stays on screen until something newer replaces it.
 */
export function useFetch<T>(path: string | null, pollMs = 0): Fetched<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(path !== null)
  const requestId = useRef(0)
  /// Whether an attempt against the CURRENT path has finished, either way.
  const settled = useRef(false)

  const load = useCallback(async () => {
    if (path === null) return
    const id = ++requestId.current
    // Only before the first result for this path. A failed first attempt
    // counts: after it the error is what should be on screen, not a spinner
    // that reappears every retry.
    if (!settled.current) setLoading(true)
    try {
      const result = await api.get<T>(path)
      if (id !== requestId.current) return
      setData(result)
      setError(null)
    } catch (err) {
      if (id !== requestId.current) return
      setError(err instanceof ApiError ? err.detail : String(err))
    } finally {
      if (id === requestId.current) {
        settled.current = true
        setLoading(false)
      }
    }
  }, [path])

  useEffect(() => {
    // A new path is a new question, so its first answer gets the placeholder
    // again rather than showing the previous path's result as though it were
    // this one's.
    settled.current = false
    if (path === null) {
      setLoading(false)
      return
    }
    setLoading(true)
    void load()
    if (pollMs <= 0) return
    const timer = setInterval(() => void load(), pollMs)
    return () => clearInterval(timer)
  }, [load, pollMs, path])

  return { data, error, loading, reload: () => void load() }
}

export interface LiveEvent {
  channel: string
  data: Record<string, unknown>
}

/**
 * Subscribe to the daemon event WebSocket.
 *
 * The feed is advisory: it tells the console something changed so it can
 * refetch. It is never treated as the source of truth, so a dropped socket
 * degrades to polling rather than showing stale data as current.
 */
export function useEventStream(onEvent?: (event: LiveEvent) => void) {
  const [connected, setConnected] = useState(false)
  const [lastEvent, setLastEvent] = useState<LiveEvent | null>(null)
  const handlerRef = useRef(onEvent)
  handlerRef.current = onEvent

  useEffect(() => {
    let socket: WebSocket | null = null
    let retry: ReturnType<typeof setTimeout> | null = null
    let closed = false

    const connect = () => {
      if (closed) return
      const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws'
      socket = new WebSocket(`${scheme}://${window.location.host}/api/v1/events/ws`)

      socket.onopen = () => setConnected(true)
      socket.onmessage = (message) => {
        try {
          const event = JSON.parse(message.data) as LiveEvent
          setLastEvent(event)
          handlerRef.current?.(event)
        } catch {
          /* ignore malformed frame */
        }
      }
      socket.onclose = () => {
        setConnected(false)
        if (!closed) retry = setTimeout(connect, 5000)
      }
      socket.onerror = () => socket?.close()
    }

    connect()
    return () => {
      closed = true
      if (retry) clearTimeout(retry)
      socket?.close()
    }
  }, [])

  return { connected, lastEvent }
}
